import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";
import Card from "./Card";
import { PHASE_STATUS } from "../lib/helpers";

// Étapes d'un projet, vue de l'entreprise : les phases en colonnes (décision du 13/08, arbitrages du 01/10 sur
// l'ébauche « Phases en colonnes », Journal Horizon).
// - Une seule phase : pleine largeur et hauteur libre, comme avant (pastille à droite sur ordinateur, règle F1 du 18/08).
// - Plusieurs phases, écran large : colonnes d'au moins 290 px qui se partagent la largeur (à 280 px, « Demander une
//   modification » passait sur deux lignes). Quand elles ne tiennent pas, elles défilent de côté dans leur cadre, jamais
//   la page. Une colonne longue s'arrête au bas de l'écran, sous le bandeau de la fiche, et défile à l'intérieur (comme
//   Trello) ; une colonne courte garde sa hauteur.
// - Plusieurs phases, téléphone (moins de 768 px, comme le calendrier éditorial) : une phase à la fois, la suivante
//   dépasse sur le bord droit ; on glisse, ou on touche la rangée des phases, qui montre tout le chemin (état, point
//   ambre là où une étape attend l'entreprise). C'est la page qui défile, jamais l'intérieur d'une colonne (le doigt y
//   resterait coincé) ; sa hauteur suit la phase affichée.
// - À l'arrivée, la vue se place sur ce que l'entreprise a à faire : la première phase où une étape l'attend ; sinon
//   la première en cours ; si tout est terminé, la dernière ; sinon la première. Dans une colonne qui défile, sur
//   l'étape qui attend.
// props : phases ; renderEtape(etape, phase, enColonnes) → la carte d'étape (avec sa key) ; awaitingKey(phase) → clé
// de la première étape qui attend l'entreprise dans cette phase, ou null ; testid.
const NARROW_QUERY = "(max-width: 767px)";

function useNarrow() {
  const [narrow, setNarrow] = useState(() => typeof window !== "undefined" && window.matchMedia(NARROW_QUERY).matches);
  useEffect(() => {
    const m = window.matchMedia(NARROW_QUERY);
    const on = () => setNarrow(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return narrow;
}

function openingPhase(phases, awaitingKey) {
  let i = phases.findIndex((ph) => awaitingKey(ph));
  if (i < 0) i = phases.findIndex((ph) => ph.status === "en_cours");
  if (i < 0 && phases.length && phases.every((ph) => ph.status === "cloturee")) i = phases.length - 1;
  return i < 0 ? 0 : i;
}

export default function PhaseBoard({ phases = [], renderEtape, awaitingKey, testid = "phase-board" }) {
  const narrow = useNarrow();
  const reduce = useReducedMotion();
  const columns = phases.length > 1;
  const target = openingPhase(phases, awaitingKey);
  const [active, setActive] = useState(target);
  const [activeH, setActiveH] = useState(null);
  const boardRef = useRef(null);
  const colRefs = useRef([]);
  const listRefs = useRef([]);
  const opened = useRef(false);
  const innerDone = useRef(false);
  const targetKey = columns ? awaitingKey(phases[target]) : null;
  const cur = Math.min(active, phases.length - 1);
  const curRef = useRef(cur);
  curRef.current = cur;

  // À l'arrivée, une seule fois (pas après chaque rechargement de la fiche, pour ne pas déplacer la vue sous les yeux).
  useLayoutEffect(() => {
    if (!columns || opened.current) return;
    const board = boardRef.current, col = colRefs.current[target];
    if (!board || !col) return;
    opened.current = true;
    if (narrow) { board.scrollLeft = col.offsetLeft; return; } // « active » part déjà de cette phase (useState)
    if (col.offsetLeft < board.scrollLeft || col.offsetLeft + col.offsetWidth > board.scrollLeft + board.clientWidth) board.scrollLeft = col.offsetLeft;
  });

  // Écran large, à l'arrivée : la colonne qui défile se place sur l'étape qui attend l'entreprise. Sa hauteur se règle
  // encore après le premier affichage (le bandeau de la fiche est mesuré ensuite, les polices arrivent) : l'étape est
  // replacée tant que la colonne bouge, pendant deux secondes, sauf si la personne fait défiler la colonne elle-même.
  useLayoutEffect(() => {
    if (!columns || narrow || !targetKey || innerDone.current) return undefined;
    const list = listRefs.current[target];
    const el = list ? list.querySelector(`[data-etape-key="${targetKey}"]`) : null;
    if (!el) return undefined;
    const place = () => {
      const lr = list.getBoundingClientRect(), er = el.getBoundingClientRect();
      if (er.bottom > lr.bottom + 1 || er.top < lr.top - 1) list.scrollTop += er.top - lr.top - 4;
    };
    const ro = new ResizeObserver(place);
    ro.observe(list);
    [...list.children].forEach((c) => ro.observe(c));
    const finish = () => { innerDone.current = true; ro.disconnect(); };
    const timer = setTimeout(finish, 2000);
    list.addEventListener("wheel", finish, { once: true, passive: true });
    list.addEventListener("touchstart", finish, { once: true, passive: true });
    place();
    return () => {
      clearTimeout(timer);
      ro.disconnect();
      list.removeEventListener("wheel", finish);
      list.removeEventListener("touchstart", finish);
    };
  }, [columns, narrow, target, targetKey]);

  // Passage d'un écran large à un écran étroit (téléphone qu'on tourne) : le cadre revient sur la phase soulignée.
  useLayoutEffect(() => {
    if (!columns || !narrow || !opened.current) return;
    const board = boardRef.current, col = colRefs.current[curRef.current];
    if (board && col) board.scrollLeft = col.offsetLeft;
  }, [columns, narrow]);

  // Téléphone : la phase affichée suit le défilement (la rangée des phases la souligne).
  useEffect(() => {
    if (!columns || !narrow) return undefined;
    const board = boardRef.current;
    if (!board) return undefined;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const first = colRefs.current[0];
        if (!first) return;
        const step = first.offsetWidth + (parseFloat(getComputedStyle(board).columnGap) || 0);
        setActive(Math.max(0, Math.min(phases.length - 1, Math.round(board.scrollLeft / step))));
      });
    };
    board.addEventListener("scroll", onScroll, { passive: true });
    return () => { board.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, [columns, narrow, phases.length]);

  // Téléphone : la hauteur du cadre suit la phase affichée, pour que la page ne défile pas dans le vide.
  useEffect(() => {
    const col = colRefs.current[cur];
    if (!columns || !narrow || !col) { setActiveH(null); return undefined; }
    const ro = new ResizeObserver(() => setActiveH(col.offsetHeight));
    ro.observe(col);
    setActiveH(col.offsetHeight);
    return () => ro.disconnect();
  }, [columns, narrow, cur]);

  const go = (i) => {
    const board = boardRef.current, col = colRefs.current[i];
    if (!board || !col) return;
    board.scrollTo({ left: col.offsetLeft, behavior: reduce ? "auto" : "smooth" });
    setActive(i);
  };

  const header = (ph) => (
    <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
      <h2 className="font-display text-xl text-marine">{ph.name}</h2>
      <span className="text-xs uppercase tracking-wider text-marine/80">{PHASE_STATUS[ph.status]?.label}</span>
    </div>
  );

  if (!columns) {
    return (
      <div className="space-y-6" data-testid={testid}>
        {phases.map((ph) => (
          <Card key={ph.key}>
            {header(ph)}
            <div className="space-y-3">{(ph.etapes || []).map((et) => renderEtape(et, ph, false))}</div>
          </Card>
        ))}
      </div>
    );
  }

  // -mx-1 px-1 / -my-1 py-1 : marge pour le liseré des cartes (ring) et le contour de focus, que le défilement couperait.
  const list = (ph, i, scroll) => (
    <div ref={(el) => { listRefs.current[i] = el; }}
      className={`space-y-3 -mx-1 px-1 -my-1 py-1 ${scroll ? "flex-1 min-h-0 overflow-y-auto" : ""}`}>
      {(ph.etapes || []).map((et) => <div key={et.key} data-etape-key={et.key}>{renderEtape(et, ph, true)}</div>)}
    </div>
  );

  if (narrow) {
    return (
      <>
        <nav aria-label="Phases du projet" data-testid={`${testid}-path`}
          className="grid grid-flow-col auto-cols-[minmax(80px,1fr)] gap-2 mb-3 overflow-x-auto">
          {phases.map((ph, i) => {
            const on = i === cur;
            return (
              <button key={ph.key} type="button" onClick={() => go(i)} aria-current={on ? "true" : undefined}
                data-testid={`${testid}-path-${ph.key}`}
                className={`relative text-left rounded-sm border px-2.5 pt-2 pb-2.5 text-marine transition-colors ${on ? "bg-creme border-marine/30" : "border-marine/15"}`}>
                <span className="block text-[13px] font-semibold truncate pr-3">{ph.name}</span>
                <span className="block mt-0.5 text-[10px] uppercase tracking-[0.08em] text-marine/80">{PHASE_STATUS[ph.status]?.label}</span>
                {awaitingKey(ph) && (
                  <span className="absolute top-2 right-2 w-2 h-2 rounded-full bg-ambre" data-testid={`${testid}-path-waiting-${ph.key}`}>
                    <span className="sr-only">Une étape attend votre validation</span>
                  </span>
                )}
                {on && <span aria-hidden="true" className="absolute left-2.5 right-2.5 -bottom-px h-0.5 rounded-full bg-bordeaux" />}
              </button>
            );
          })}
        </nav>
        {/* Sous 360 px, colonne à 90 % et écart de 12 px : à 86 %, « Valider cette étape » passait sur deux lignes (143 px
            nécessaires, 142 disponibles à 320 px) ; la phase suivante dépasse toujours sur le bord. */}
        <div ref={boardRef} data-testid={testid} className="relative flex gap-4 max-[359px]:gap-3 items-start overflow-x-auto snap-x snap-mandatory"
          style={{ height: activeH || undefined, overflowY: "hidden" }}>
          {phases.map((ph, i) => (
            <section key={ph.key} ref={(el) => { colRefs.current[i] = el; }} aria-label={ph.name} data-testid={`phase-col-${ph.key}`}
              className="shrink-0 basis-[86%] max-[359px]:basis-[90%] snap-start">
              <Card>{header(ph)}{list(ph, i, false)}</Card>
            </section>
          ))}
        </div>
      </>
    );
  }

  // --record-sticky-top : bas du bandeau collant de la fiche + marge (RecordShell) ; la colonne s'arrête 16 px avant le bas.
  return (
    <div ref={boardRef} data-testid={testid} className="relative flex gap-4 items-start overflow-x-auto pb-2">
      {phases.map((ph, i) => (
        <section key={ph.key} ref={(el) => { colRefs.current[i] = el; }} aria-label={ph.name} data-testid={`phase-col-${ph.key}`}
          className="flex-[1_1_290px] min-w-[290px] flex flex-col"
          style={{ maxHeight: "calc(100vh - var(--record-sticky-top, 140px) - 16px)" }}>
          <Card className="flex flex-col min-h-0">{header(ph)}{list(ph, i, true)}</Card>
        </section>
      ))}
    </div>
  );
}
